/**
 * useKeywordScoring Hook (v2)
 * 
 * Client-side hook for applying categorized keyword-based scoring.
 * 
 * Two keyword categories:
 * 1. Role Keywords - match job titles, skills, descriptions, certifications
 * 2. Company Keywords - match company name, industry, description
 * 
 * NO hidden semantic expansion - exact match only for transparency.
 * Users see exactly what they searched for.
 */

import { useState, useCallback, useMemo } from 'react';

// Scoring weights by field
const ROLE_WEIGHTS = {
  jobTitle: 200,
  skills: 100,
  description: 50,
};

const COMPANY_WEIGHTS = {
  companyName: 200,
  industry: 150,
  companyDescription: 75,
};

/**
 * Safely convert a value to a lowercase string
 * Handles strings, arrays, objects, null, undefined
 */
function toSearchableString(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.toLowerCase();
  if (Array.isArray(value)) return value.map(v => toSearchableString(v)).join(' ');
  if (typeof value === 'object') {
    return toSearchableString(value.name || value.label || value.value || '');
  }
  return String(value).toLowerCase();
}

/**
 * Check if text contains keyword (case-insensitive, word boundary aware)
 */
function textContainsKeyword(text, keyword) {
  if (!text || !keyword) return false;
  const lowerText = text.toLowerCase();
  const lowerKeyword = keyword.toLowerCase();
  return lowerText.includes(lowerKeyword);
}

/**
 * Find role keyword matches in a result
 * Searches: job title, skills, certifications, description
 */
function findRoleMatches(result, keywords) {
  if (!keywords || keywords.length === 0) return { matches: [], fields: {} };
  
  // Handle nested data structures (result.data.jobTitle, result.lead_data.jobTitle, etc.)
  const data = result.data || result.lead_data || result;
  
  const jobTitle = toSearchableString(
    data.jobTitle || data.job_title || data.title || 
    result.jobTitle || result.job_title || result.title
  );
  const skills = toSearchableString(
    data.skills || data.skillset || 
    result.skills || result.skillset || []
  );
  const description = toSearchableString(
    data.description || data.summary || data.bio ||
    result.description || result.summary || result.bio
  );
  
  const matches = [];
  const fields = { jobTitle: [], skills: [], description: [] };
  
  for (const keyword of keywords) {
    if (textContainsKeyword(jobTitle, keyword)) {
      matches.push(keyword);
      fields.jobTitle.push(keyword);
    }
    if (textContainsKeyword(skills, keyword)) {
      if (!matches.includes(keyword)) matches.push(keyword);
      fields.skills.push(keyword);
    }
    if (textContainsKeyword(description, keyword)) {
      if (!matches.includes(keyword)) matches.push(keyword);
      fields.description.push(keyword);
    }
  }
  
  return { matches: [...new Set(matches)], fields };
}

/**
 * Find company keyword matches in a result
 * Searches: company name, industry, company description
 */
function findCompanyMatches(result, keywords) {
  if (!keywords || keywords.length === 0) return { matches: [], fields: {} };
  
  // Handle nested data structures
  const data = result.data || result.lead_data || result;
  
  const companyName = toSearchableString(
    data.company?.name || data.companyName || data.company_name ||
    result.company?.name || result.companyName || result.company_name
  );
  const industry = toSearchableString(
    data.company?.primaryIndustry || data.primaryIndustry || data.industry ||
    result.company?.primaryIndustry || result.primaryIndustry || result.industry
  );
  const companyDescription = toSearchableString(
    data.company?.description || data.companyDescription || data.company?.descriptionList ||
    result.company?.description || result.companyDescription || result.company?.descriptionList
  );
  
  const matches = [];
  const fields = { companyName: [], industry: [], companyDescription: [] };
  
  for (const keyword of keywords) {
    if (textContainsKeyword(companyName, keyword)) {
      matches.push(keyword);
      fields.companyName.push(keyword);
    }
    if (textContainsKeyword(industry, keyword)) {
      if (!matches.includes(keyword)) matches.push(keyword);
      fields.industry.push(keyword);
    }
    if (textContainsKeyword(companyDescription, keyword)) {
      if (!matches.includes(keyword)) matches.push(keyword);
      fields.companyDescription.push(keyword);
    }
  }
  
  return { matches: [...new Set(matches)], fields };
}

/**
 * Calculate score from matches
 */
function calculateRoleScore(fields) {
  let score = 0;
  score += (fields.jobTitle?.length || 0) * ROLE_WEIGHTS.jobTitle;
  score += (fields.skills?.length || 0) * ROLE_WEIGHTS.skills;
  score += (fields.description?.length || 0) * ROLE_WEIGHTS.description;
  return score;
}

function calculateCompanyScore(fields) {
  let score = 0;
  score += (fields.companyName?.length || 0) * COMPANY_WEIGHTS.companyName;
  score += (fields.industry?.length || 0) * COMPANY_WEIGHTS.industry;
  score += (fields.companyDescription?.length || 0) * COMPANY_WEIGHTS.companyDescription;
  return score;
}

/**
 * Main hook - Categorized Keyword Scoring
 */
export function useKeywordScoring(results = []) {
  const [roleKeywords, setRoleKeywords] = useState([]);
  const [companyKeywords, setCompanyKeywords] = useState([]);
  
  // Score and sort results
  const scoredResults = useMemo(() => {
    if (!results || results.length === 0) {
      return { 
        results: [], 
        roleMatchCount: 0, 
        companyMatchCount: 0,
        totalMatchCount: 0 
      };
    }
    
    const hasRoleKeywords = roleKeywords.length > 0;
    const hasCompanyKeywords = companyKeywords.length > 0;
    
    if (!hasRoleKeywords && !hasCompanyKeywords) {
      // No keywords - return results unchanged
      return {
        results: results.map(r => ({
          ...r,
          _keywordScore: 0,
          _roleMatches: null,
          _companyMatches: null,
          _hasKeywordMatch: false,
        })),
        roleMatchCount: 0,
        companyMatchCount: 0,
        totalMatchCount: 0,
      };
    }
    
    // Score each result
    const scored = results.map(result => {
      const roleResult = findRoleMatches(result, roleKeywords);
      const companyResult = findCompanyMatches(result, companyKeywords);
      
      const roleScore = calculateRoleScore(roleResult.fields);
      const companyScore = calculateCompanyScore(companyResult.fields);
      const totalScore = roleScore + companyScore;
      
      const hasRoleMatch = roleResult.matches.length > 0;
      const hasCompanyMatch = companyResult.matches.length > 0;
      const hasKeywordMatch = hasRoleMatch || hasCompanyMatch;
      
      return {
        ...result,
        _keywordScore: totalScore,
        _roleMatches: hasRoleMatch ? roleResult : null,
        _companyMatches: hasCompanyMatch ? companyResult : null,
        _hasKeywordMatch: hasKeywordMatch,
        _hasRoleMatch: hasRoleMatch,
        _hasCompanyMatch: hasCompanyMatch,
        _originalScore: result._rankingScore || result.score || 0,
        _boostedScore: (result._rankingScore || result.score || 0) + totalScore,
        // Legacy compatibility
        _keywordMatches: {
          jobTitle: roleResult.fields.jobTitle || [],
          companyName: companyResult.fields.companyName || [],
          companyDescription: companyResult.fields.companyDescription || [],
          primaryIndustry: companyResult.fields.industry || [],
        },
      };
    });
    
    // Sort by boosted score (descending)
    scored.sort((a, b) => (b._boostedScore || 0) - (a._boostedScore || 0));
    
    const roleMatchCount = scored.filter(r => r._hasRoleMatch).length;
    const companyMatchCount = scored.filter(r => r._hasCompanyMatch).length;
    const totalMatchCount = scored.filter(r => r._hasKeywordMatch).length;
    
    return { results: scored, roleMatchCount, companyMatchCount, totalMatchCount };
  }, [results, roleKeywords, companyKeywords]);
  
  // Role keyword management
  const addRoleKeyword = useCallback((keyword) => {
    const kw = keyword.toLowerCase().trim();
    if (kw && !roleKeywords.includes(kw)) {
      setRoleKeywords(prev => [...prev, kw]);
    }
  }, [roleKeywords]);
  
  const removeRoleKeyword = useCallback((keyword) => {
    setRoleKeywords(prev => prev.filter(k => k !== keyword));
  }, []);
  
  // Company keyword management
  const addCompanyKeyword = useCallback((keyword) => {
    const kw = keyword.toLowerCase().trim();
    if (kw && !companyKeywords.includes(kw)) {
      setCompanyKeywords(prev => [...prev, kw]);
    }
  }, [companyKeywords]);
  
  const removeCompanyKeyword = useCallback((keyword) => {
    setCompanyKeywords(prev => prev.filter(k => k !== keyword));
  }, []);
  
  // Clear all
  const clearAllKeywords = useCallback(() => {
    setRoleKeywords([]);
    setCompanyKeywords([]);
  }, []);
  
  // Legacy compatibility - single keyword list
  const allKeywords = useMemo(() => 
    [...roleKeywords, ...companyKeywords], 
    [roleKeywords, companyKeywords]
  );
  
  const addKeyword = useCallback((keyword) => {
    // Default to role keywords for legacy callers
    addRoleKeyword(keyword);
  }, [addRoleKeyword]);
  
  const removeKeyword = useCallback((keyword) => {
    removeRoleKeyword(keyword);
    removeCompanyKeyword(keyword);
  }, [removeRoleKeyword, removeCompanyKeyword]);
  
  return {
    // Scored results
    results: scoredResults.results,
    
    // Match counts
    roleMatchCount: scoredResults.roleMatchCount,
    companyMatchCount: scoredResults.companyMatchCount,
    totalMatchCount: scoredResults.totalMatchCount,
    matchCount: scoredResults.totalMatchCount, // Legacy
    
    // Categorized keyword management
    roleKeywords,
    companyKeywords,
    addRoleKeyword,
    removeRoleKeyword,
    addCompanyKeyword,
    removeCompanyKeyword,
    clearAllKeywords,
    
    // Legacy compatibility
    keywords: allKeywords,
    addKeyword,
    removeKeyword,
    clearKeywords: clearAllKeywords,
    
    // Metadata
    hasKeywords: roleKeywords.length > 0 || companyKeywords.length > 0,
    hasRoleKeywords: roleKeywords.length > 0,
    hasCompanyKeywords: companyKeywords.length > 0,
  };
}

/**
 * Get match context for display - shows WHERE the match occurred
 */
export function getMatchContext(result) {
  const contexts = [];
  
  if (result._roleMatches?.fields) {
    const { jobTitle, skills } = result._roleMatches.fields;
    if (jobTitle?.length > 0) {
      const actualTitle = result.jobTitle || result.job_title || result.title || '';
      contexts.push({ 
        type: 'role',
        field: 'Title', 
        keyword: jobTitle[0],
        context: actualTitle,
      });
    }
    if (skills?.length > 0) {
      contexts.push({ 
        type: 'role',
        field: 'Skills', 
        keyword: skills[0],
        context: null,
      });
    }
  }
  
  if (result._companyMatches?.fields) {
    const { companyName, industry } = result._companyMatches.fields;
    if (companyName?.length > 0) {
      const actualCompany = result.companyName || result.company_name || result.company?.name || '';
      contexts.push({ 
        type: 'company',
        field: 'Company', 
        keyword: companyName[0],
        context: actualCompany,
      });
    }
    if (industry?.length > 0) {
      const actualIndustry = result.industry || result.primaryIndustry || result.company?.primaryIndustry || '';
      contexts.push({ 
        type: 'company',
        field: 'Industry', 
        keyword: industry[0],
        context: typeof actualIndustry === 'string' ? actualIndustry : actualIndustry?.name || '',
      });
    }
  }
  
  return contexts;
}

export default useKeywordScoring;
